// ── ONE client-side reading of the counterparty rule ────────────────────────
//
// OPERATOR RULE (2026-08-10, verbatim): "if we are trying to link/mark a
// transaction, we must first link the wallet with a customer/a label, unless it
// is an address with public tag, which means, we cannot link a transaction/mark
// a transaction with an anonymous address."
//
// So an ON-CHAIN transaction may only be assigned to a customer or marked as an
// internal swap when its COUNTERPARTY — the side that is NOT our monitored
// address — is identified: it is one of OUR OWN monitored wallets, or it
// carries an address label (customer link or remark), or a block-explorer
// public tag.
//
// AUTHORITY: the server. Every enforcing endpoint answers 409
// { code: 'ADDRESS_ANONYMOUS', address, chain } from ONE shared helper. This
// module exists so the UI never OFFERS a click the server will refuse: a refused
// click would only come back as an error toast, so the row withholds the button
// and says up front what to do instead (label the address first).
//
// READ-BY-REFERENCE: when the list endpoint projected `row.counterparty` (the
// server's own verdict) we render THAT and re-derive nothing. Only when the
// field is absent do we fall back to the shared address-label resolver
// (components/AddressLabel's useAddressLabels), which is itself built from the
// SAME three server tables — labels, our monitored addresses, public tags.
// Same shape as txActionMatrix's documented consumed_by fallback.

export const COUNTERPARTY_BLOCK_MSG =
  'Label this address or link it to a customer first — transactions with anonymous addresses cannot be linked or marked. (Addresses with a public tag are exempt.)';

export const COUNTERPARTY_BLOCK_SHORT = 'Counterparty address not labelled';

// Is this row an on-chain row at all? Only on-chain rows have a counterparty.
// The four shapes in the app: /transaction-history rows carry source, the
// crypto-wallet modal carries transaction_source, the address feed carries
// tx_hash + address_id and nothing else, and the order pickers
// (/crypto-transactions/unlinked, used by Create Order and the Order History edit
// modal) carry NO source field at all — an on-chain row there is recognised by
// its non-empty blockchain_tx_id (the crypto_transactions mirror of a transfer;
// manual crypto rows have none, so they are correctly left ungated).
export function isChainRow(row) {
  if (!row) return false;
  if (row.source === 'blockchain' || row.transaction_source === 'blockchain') return true;
  if (row.source !== undefined || row.transaction_source !== undefined) return false;
  if (row.tx_hash != null && row.address_id != null) return true;
  return String(row.blockchain_tx_id ?? '').trim() !== '';
}

// Which side of the transfer is the counterparty? from_address on an inflow,
// to_address on an outflow. Surfaces label direction either on-chain
// (incoming/outgoing) or ledger-style (credit/debit) — both are accepted.
export function counterpartyAddressOf(row) {
  if (!row) return { address: null, chain: null };
  const t = row.transaction_type;
  const incoming = t === 'incoming' || t === 'credit';
  return {
    address: (incoming ? row.from_address : row.to_address) || null,
    chain: row.chain || row.blockchain_network || null,
  };
}

// The gate. `resolve` is useAddressLabels().resolve — pass it so the fallback
// path can answer; omit it and a row without a server projection is treated as
// UNKNOWN (never blocked in the UI, still refused by the server if anonymous).
//
//   { applies, blocked, address, chain, via, label, message }
//
//     applies  — this row is subject to the rule at all (an on-chain row)
//     blocked  — the counterparty is anonymous ⇒ do not offer assign / mark-swap
//     via      — how it was identified: own | customer | label | public_tag
export function counterpartyGate(row, resolve) {
  if (!isChainRow(row)) {
    return { applies: false, blocked: false, address: null, chain: null, via: null, label: null, message: null };
  }

  // Server projection wins — it IS the enforcing verdict.
  const projected = row.counterparty;
  if (projected && typeof projected === 'object') {
    return {
      applies: true,
      blocked: !projected.identified,
      address: projected.address || null,
      chain: projected.chain || null,
      via: projected.via || null,
      label: projected.label || null,
      message: projected.identified ? null : COUNTERPARTY_BLOCK_MSG,
    };
  }

  const { address, chain } = counterpartyAddressOf(row);
  if (typeof resolve !== 'function') {
    // No projection and no resolver — say nothing rather than guess. The server
    // still refuses an anonymous counterparty.
    return { applies: true, blocked: false, address, chain, via: null, label: null, message: null };
  }
  if (!address) {
    // No counterparty recorded. The server fails CLOSED on this shape, so mirror
    // it here instead of offering a doomed action.
    return { applies: true, blocked: true, address: null, chain, via: null, label: null, message: COUNTERPARTY_BLOCK_MSG };
  }
  const info = resolve(address);
  if (!info) {
    return { applies: true, blocked: true, address, chain, via: null, label: null, message: COUNTERPARTY_BLOCK_MSG };
  }
  const via = info.label_type === 'own' ? 'own'
    : info.label_type === 'customer' ? 'customer'
    : info.label_type === 'public' ? 'public_tag'
    : 'label';
  return { applies: true, blocked: false, address, chain, via, label: info.label || null, message: null };
}
